<template>
  <v-autocomplete
    outlined
    v-model="selectedConfig"
    :items="aifAutoConfigList"
    :menu-props="{ maxHeight: '400', zIndex: zindex }"
    label="Configuration Création Auto"
    :multiple="false"
    clearable
    hide-details
    :dense="dense"
    item-text="name"
    return-object
    :loading="loading"
  />
</template>

<script>
import { computed, onMounted, reactive, watch } from "@vue/composition-api";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";
import { URL_PARAM_NAMES } from "@/utils/constants";

export default {
  name: "AifAutoConfigFilter",
  components: {},
  props: {
    site: {
      type: Number,
      default: 0,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    zindex: {
      type: Number,
      default: 13,
    },
  },
  setup(props) {
    const { axiosGet } = useAxios();
    const { store } = useProxy();
    const { addQueryStringParam, synchronizeFilterWithQueryString } =
      useQueryString();

    const page = reactive({
      aifAutoConfigList: [],
      isInitialized: false,
      loading: false,
    });

    const loading = computed(() => page.loading);
    const aifAutoConfigList = computed(() => page.aifAutoConfigList);

    const selectedConfig = computed({
      get: () => store.getters["newsRadar/getAifAutoConfig"],
      set: (value) => {
        store.dispatch("newsRadar/updateAifAutoConfig", value || null);
        addQueryStringParam({
          param: URL_PARAM_NAMES.AifAutoConfigFilter,
          value: value?.id ?? "",
        });
      },
    });

    const fetchList = async () => {
      page.loading = true;
      try {
        const {
          data: { items },
        } = await axiosGet("/aif-auto-configs/options", {
          ...(props.site ? { site_id: props.site } : {}),
        });
        page.aifAutoConfigList = items || [];
      } catch (e) {
        page.aifAutoConfigList = [];
      } finally {
        page.loading = false;
      }
    };

    watch(
      () => props.site,
      async () => {
        if (page.isInitialized) {
          await fetchList();
        }
      }
    );

    onMounted(async () => {
      const urlConfigId = await synchronizeFilterWithQueryString({
        dispatcher: "",
        param: URL_PARAM_NAMES.AifAutoConfigFilter,
        value: store.getters["newsRadar/getAifAutoConfig"]?.id ?? "",
        is_integer: true,
        dependsOn: URL_PARAM_NAMES.SiteFilter,
      });

      const fetchedSiteId = props.site;
      await fetchList();

      const selectedConfigId =
        urlConfigId ?? store.getters["newsRadar/getAifAutoConfig"]?.id;
      if (selectedConfigId && page.aifAutoConfigList.length) {
        const listItem = page.aifAutoConfigList.find(
          (config) => config.id === selectedConfigId
        );
        if (listItem) {
          await store.dispatch("newsRadar/updateAifAutoConfig", listItem);
        }
      }

      page.isInitialized = true;

      // The parent SiteFilter can finish restoring its query-string value while
      // this component is mounting. Its watcher deliberately skips that phase,
      // so catch up once initialization is complete.
      if (props.site !== fetchedSiteId) {
        await fetchList();
      }
    });

    return {
      aifAutoConfigList,
      loading,
      selectedConfig,
    };
  },
};
</script>

<style scoped></style>
